import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CommonModule } from '@angular/common';
import { EditElectricityInvoiceComponent } from "../../../invoice/components/edit-electricity-invoice/edit-electricity-invoice.component";
import { CommodityType, InvoiceBimestraleGet, InvoiceElectricityAndCompanyGet, InvoiceStatus } from '../../../../core/interfaces/invoice-interfaces';
import { Location } from '../../../../core/interfaces/location';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { CompanyInformationComponent } from "../company-information/company-information.component";
import { FilesService } from '../../../../core/api/files.service';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { EditBimestraleSummaryComponent } from '../edit-bimestrale-summary/edit-bimestrale-summary.component';

@Component({
  selector: 'app-admin-review-expanded-electricity-invoice',
  standalone: true,
  imports: [CommonModule, NgxExtendedPdfViewerModule, EditElectricityInvoiceComponent, TranslocoPipe, CompanyInformationComponent, CustomButtonComponent, EditBimestraleSummaryComponent],
  templateUrl: './admin-review-expanded-electricity-invoice.component.html',
  styleUrl: './admin-review-expanded-electricity-invoice.component.css'
})
export class AdminReviewExpandedElectricityInvoiceComponent implements OnChanges, OnInit {
  constructor(
    private invoiceService: InvoiceService,
    private apiError: ApiErrorService,
    private filesService: FilesService,
    private sanitizer: DomSanitizer
  ) { }

  // Toast
  @Input() toast!: ToastComponent;

  @Input() fileUrl?: string;
  @Input() fileId?: string;

  safePdfUrl?: SafeResourceUrl

  CommodityType = CommodityType;

  // Locations
  @Input() listOfLocations: Location[] = [];

  // Callback
  @Input() reloadCallbackFunction!: () => void;
  @Input() createLocationSuccessCallbackFunction!: () => void;
  @Input() hardReloadCallbackFunction!: () => void;

  wrappedReloadCallback = () => {
    this.reloadCallbackFunction();
    if (this.fileId) this.loadInvoicesByFile(this.fileId, false);
  };

  // Multi-period support
  fileInvoices: InvoiceElectricityAndCompanyGet[] = [];
  loadingInvoices: boolean = false;
  bimestraleData: InvoiceBimestraleGet | null = null;

  get firstInvoice(): InvoiceElectricityAndCompanyGet | undefined {
    return this.fileInvoices.length > 0 ? this.fileInvoices[0] : undefined;
  }

  get isMultiPeriod(): boolean {
    return this.fileInvoices.length > 1;
  }

  @ViewChildren('editElectricity') editElectricityElements?: QueryList<EditElectricityInvoiceComponent>;

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileId']) {
      if (this.fileId) {
        this.filesService.getPdfUrl(this.fileId).subscribe(response => {
          if (response.download_url) this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.download_url);
        });

        this.loadInvoicesByFile(this.fileId);
      }
    }
  }

  async loadInvoicesByFile(fileId: string, showLoading: boolean = true) {
    if (showLoading) this.loadingInvoices = true;
    try {
      const invoices = await lastValueFrom(this.invoiceService.getElectricityInvoicesByFile(fileId));
      this.fileInvoices = (invoices || []).sort((a, b) => {
        const dateA = a.for_period ? new Date(a.for_period).getTime() : 0;
        const dateB = b.for_period ? new Date(b.for_period).getTime() : 0;
        return dateA - dateB;
      });

      if (this.isMultiPeriod) {
        try {
          this.bimestraleData = await lastValueFrom(this.invoiceService.getBimestraleByFile(fileId));
        } catch (e) {
          this.bimestraleData = null;
        }
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.fileInvoices = [];
    }
    this.loadingInvoices = false;
  }

  async downloadDisputeReport(invoice: InvoiceElectricityAndCompanyGet) {
    if (!invoice) return;

    if (invoice.status == InvoiceStatus.CALCULATION_FAILED) {
      this.toast.showToast("shared.error", "invoice.not_enough_data", 'error', 5000, true);
      return;
    }

    try {
      var responseBlob = await lastValueFrom(this.invoiceService.getElectricityDispute(invoice.id ?? ''));
      const blobUrl = URL.createObjectURL(responseBlob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
  }

  formatPeriod(forPeriod: string | null): string {
    if (!forPeriod) return '';
    const match = forPeriod.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[2]}/${match[1]}`;
    return forPeriod;
  }

  scroolToSpread() {
    const elements = this.editElectricityElements?.toArray();
    if (elements && elements.length > 0) {
      elements[0].scroolToSpread();
    }
  }

  onBimestraleSaved() {
    if (this.fileId) this.loadInvoicesByFile(this.fileId);
  }

  onBimestraleCancelled() {
    if (this.fileId) this.loadInvoicesByFile(this.fileId);
  }
}
